import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  byId: {
    // [orderId]: {
    //   connected: false,
    //   status: "active" | "completed",
    //   lastUpdateTs: null,
    //   etaStore: null,
    //   driver: { lat: null, lng: null, speed: null, heading: null, ts: null },
    //   dropoff: null, // { lat, lng }
    //   milestones: [], // [{ key, type, label, ts, source }]
    //   dedupeKeys: {}, // { [key]: true }
    // }
  }
};

const milestoneLabels = {
  tracking_start: "Starting order update feed",
  store_eta_15: "Driver is 15 mins away from store for pick-up",
  reachedStore: "Arrived to pick-up and load groceries",
  leftStore: "On the way to the unit",
  reachedHome: "Arrived and Stocking Property",
  delivered: "Order has been Stocked and Delivered.",
};

function ensure(state, orderId) {
  if (!state.byId[orderId]) {
    state.byId[orderId] = {
      connected: false,
      status: "active",
      lastUpdateTs: null,
      etaStore: null,
      driver: { lat: null, lng: null, speed: null, heading: null, ts: null },
      dropoff: null,
      milestones: [],
      dedupeKeys: {}
    };
  }
  return state.byId[orderId];
}

function addMilestone(entry, type, ts, source, labelOverride) {
  const safeTs = ts || Date.now();
  const key = `${type}-${safeTs}`;
  if (entry.dedupeKeys[key]) return;
  entry.dedupeKeys[key] = true;
  entry.milestones.push({
    key,
    type,
    ts: safeTs,
    source: source || "geo",
    label: labelOverride || milestoneLabels[type] || type
  });
}

const orderTrackingSlice = createSlice({
  name: "orderTracking",
  initialState,
  reducers: {
    trackingConnected(state, { payload: { orderId } }) {
      const e = ensure(state, orderId);
      e.connected = true;
    },
    trackingDisconnected(state, { payload: { orderId } }) {
      const e = ensure(state, orderId);
      e.connected = false;
    },
    trackingEnded(state, { payload: { orderId, ts } }) {
      const e = ensure(state, orderId);
      e.status = "completed";
      addMilestone(e, "delivered", ts || Date.now(), "geo");
    },
    snapshotReceived(state, { payload: { orderId, dropoff, driverStatus } }) {
      const e = ensure(state, orderId);
      if (dropoff?.lat && dropoff?.lng) e.dropoff = { lat: dropoff.lat, lng: dropoff.lng };
      if (Array.isArray(driverStatus)) {
        driverStatus.forEach(ds => {
          if (!ds?.status) return;
          addMilestone(e, ds.status, ds.timestamp || Date.now(), "button");
        });
      }
      addMilestone(e, "tracking_start", Date.now(), "geo");
    },
    dropoffReceived(state, { payload: { orderId, lat, lng } }) {
      const e = ensure(state, orderId);
      if (typeof lat === "number" && typeof lng === "number") {
        e.dropoff = { lat, lng };
      }
    },
    driverLocationReceived(state, { payload: { orderId, lat, lng, speed = null, heading = null, ts = Date.now() } }) {
      const e = ensure(state, orderId);
      e.driver = { lat, lng, speed, heading, ts };
      e.lastUpdateTs = ts;
    },
    storeEta15Received(state, { payload: { orderId, etaMinutes, ts = Date.now() } }) {
      const e = ensure(state, orderId);
      e.etaStore = typeof etaMinutes === "number" ? Math.max(0, Math.round(etaMinutes)) : null;
      addMilestone(e, "store_eta_15", ts, "geo");
    },
    milestoneReceived(state, { payload: { orderId, type, ts, source, label } }) {
      const e = ensure(state, orderId);
      addMilestone(e, type, ts, source, label);
    },
    orderUpdateReceived(state, { payload: { orderId, change } }) {
      const updated = change?.updateDescription?.updatedFields || {};
      const e = ensure(state, orderId);

      Object.keys(updated).forEach((field) => {
        if (field.startsWith("driverStatus") && updated[field]) {
          const val = updated[field];
          const ts = val.timestamp || Date.now();
          const type = val.status;
          if (type) addMilestone(e, type, ts, "button");
        }
        if (field === "status" && updated[field] === "completed") {
          e.status = "completed";
        }
      });
    },
    resetOrderTracking(state, { payload: { orderId } }) {
      delete state.byId[orderId];
    }
  }
});

export const {
  trackingConnected,
  trackingDisconnected,
  trackingEnded,
  snapshotReceived,
  dropoffReceived,
  driverLocationReceived,
  storeEta15Received,
  milestoneReceived,
  orderUpdateReceived,
  resetOrderTracking
} = orderTrackingSlice.actions;

export default orderTrackingSlice.reducer;

// Selectors
const selectDomain = (state) => state.orderTracking || initialState;
export const selectTrackingById = (state, orderId) => selectDomain(state).byId[orderId] || null;

export const selectDriverLoc = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => entry?.driver || null
);

export const selectEtaStore = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => entry?.etaStore ?? null
);

export const selectStatus = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => entry?.status || "active"
);

export const selectConnected = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => Boolean(entry?.connected)
);

export const selectDropoff = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => entry?.dropoff || null
);

export const selectOrderedTimeline = createSelector(
  [(state, orderId) => selectTrackingById(state, orderId)],
  (entry) => {
    if (!entry) return [];
    const order = [
      "tracking_start",
      "store_eta_15",
      "reachedStore",
      "leftStore",
      "reachedHome",
      "delivered",
    ];
    const earliestByType = {};
    entry.milestones.forEach(m => {
      if (!earliestByType[m.type] || (m.ts && m.ts < earliestByType[m.type].ts)) {
        earliestByType[m.type] = m;
      }
    });
    return order.map(type => earliestByType[type] || { type, label: milestoneLabels[type], ts: null, source: null, key: type });
  }
);
